import { useContext, Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Container } from 'reactstrap';
import { Button } from 'reactstrap';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { TenantContext } from '../../../context/TenantContext';
import { UserEntries } from './UserEntries';
import { ModalUserAddOrUpdate } from './ModalUserAddOrUpdate';
import { ModalUserTokensUpdate } from './ModalUserTokensUpdate';
import * as Utils from '../../../utils/helper';
import * as Misc from '../../../utils/misc';
import * as apiCalls from '../../../logic/apiCalls';
import { ButtonWithDisabledTooltip } from '../../../components/ButtonWithDisabledTooltip';
import { Alert } from 'react-bootstrap';

// The maximum allowed number of users per tenant.
const maximumNumberOfUsers = 100;

class UserSettingsInternal extends Component {
    state = {
        isLoading: true,
        users: [],
        userToDelete: {},
        userToUpdate: {},
        isConfirmDeleteModalOpen: false,
        isUserModalOpen: false,
        isUserModalUpdate: false,
        isUserTokensModalOpen: false,
    }

    async componentDidMount() {
        this.getUsers();
    }

    async componentWillUnmount() {
    }

    showConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: true,
        });
    };

    hideConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: false,
        });
    };

    showAddUserModal = () => {
        this.setState({
            isUserModalUpdate: false,
            isUserModalOpen: true,
        });
    };

    showUpdateUserModal = () => {
        this.setState({
            isUserModalUpdate: true,
            isUserModalOpen: true,
        });
    };

    hideUserModal = () => {
        this.setState({
            isUserModalOpen: false,
        });
    }

    showUpdateUserTokensModal = () => {
        this.setState({
            isUserTokensModalOpen: true,
        });
    }

    hideUpdateUserTokensModal = () => {
        this.setState({
            isUserTokensModalOpen: false,
        });
    }
    
    getUsers = async () => {
        try {
            console.log("[getUsers] Invoking the back-end API to get all users of this tenant.");

            const users = await apiCalls.getUsers(this.props.authenticationContext, this.props.tenantContext.tenantId);

            this.setState({
                users: users,
            });               
        }
        catch (err) {
            console.log(`[getUsers] Error retrieving the users: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
        finally {
            this.setState({
                isLoading: false,
            });
        }
    }

    handleUserDelete = (user) => {
        this.setState({
            userToDelete: user,
        });
        this.showConfirmDeleteModal();
    }

    handleUserEdit = (user) => {
        this.setState({
            userToUpdate: user,
        });
        this.showUpdateUserModal();
    }

    handleTokensEdit = (user) => {
        this.setState({
            userToUpdate: user,
        });
        this.showUpdateUserTokensModal();
    }

    executeDeleteUser = async () => {
        try {
            console.log(`[deleteUser] Invoking the back-end API to delete the user with Id ${this.state.userToDelete.userId}.`);

            await apiCalls.deleteUser(this.props.authenticationContext, this.props.tenantContext.tenantId, this.state.userToDelete.userId);

            this.props.notificationContext.setNotification("Deleting user.", `'${this.state.userToDelete.name}' successfully deleted.`, 'success');

            this.getUsers();
        }
        catch (err) {
            this.props.notificationContext.setNotification("Deleting user.", `Error deleting '${this.state.userToDelete.name}'. ${err.toDetailedMessage()}`, 'danger');                  
        }

        this.hideConfirmDeleteModal();
    }

    executeAddOrUpdate = async (doneInfo) => {
        const userAction = doneInfo.isUpdate ? 'updating' : 'adding';
        const userActionDone = doneInfo.isUpdate ? 'updated' : 'added';

        try {
            console.log(`[executeAddOrUpdate] Invoking the back-end API ${userAction} the user.`);

            if (doneInfo.isUpdate)
            {
                await apiCalls.updateUser(this.props.authenticationContext, this.props.tenantContext.tenantId, doneInfo.userId, doneInfo.name, doneInfo.description);
            }
            else
            {
                await apiCalls.addUser(this.props.authenticationContext, this.props.tenantContext.tenantId, doneInfo.name, doneInfo.description);
            }

            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(userAction)} user.`, `'${doneInfo.name}' successfully ${userActionDone}.`, 'success');
            this.getUsers();
        }
        catch (err) {
            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(userAction)} user.`, `Error ${userAction} '${doneInfo.name}'. ${err.toDetailedMessage()}`, 'danger');
        }
        finally {
            this.hideUserModal();
        }
    }

    shouldAddUserButtonBeEnabled = () => {
        if (this.state.users.length >= maximumNumberOfUsers) {
            return false;
        }

        return true;
    }

    // ------------------------------
    // Rendering starts here...
    // ------------------------------
    render () {
        const headerRendered = <h1>User settings</h1> 

        if (this.state.isLoading) {
            return (
                <Container fluid>
                    {headerRendered}
                    {this.props.tenantContext.showWarningWhenDisabled()}
                    <p><em>Loading...</em></p>
                </Container>
            );
        }

        const warningText = Utils.isTrue(this.state.userToDelete.enabled) ? `The user '${this.state.userToDelete.name}' will no longer be notified of any events.` : "";
        
        const confirmDeleteModalRendered = <>
            <Modal show={this.state.isConfirmDeleteModalOpen} onHide={this.hideConfirmDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete user '{this.state.userToDelete.name}'?<br/>    
                    {warningText}           
                </Modal.Body>
                <Modal.Footer>
                    <Button color="secondary" onClick={this.hideConfirmDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={this.executeDeleteUser}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>

        const users = this.state.users;

        const usersRendered = users.length > 0 ? 
            <UserEntries users={users} handleDelete={this.handleUserDelete} handleEdit={this.handleUserEdit} handleTokensEdit={this.handleTokensEdit}/> : 
            <Alert className="mt-3" variant='warning' transition={false}>
                No users configured. Please add a user which will be notified by an intercom.
            </Alert>;

        return (
            <Container fluid>
                {confirmDeleteModalRendered}
                <ModalUserAddOrUpdate isOpen={this.state.isUserModalOpen} isUpdate={this.state.isUserModalUpdate} tenant={this.props.tenantContext} userToUpdate={this.state.userToUpdate} onHide={this.hideUserModal} onAddOrUpdate={this.executeAddOrUpdate}/>
                <ModalUserTokensUpdate isOpen={this.state.isUserTokensModalOpen} tenant={this.props.tenantContext} userToUpdate={this.state.userToUpdate} onHide={this.hideUpdateUserTokensModal}/>
                {headerRendered}
                {this.props.tenantContext.showWarningWhenDisabled()}
                <hr/>
                <ButtonWithDisabledTooltip color="primary" disabled={!this.shouldAddUserButtonBeEnabled()} disabledTooltip="Maximum number of users reached" onClick={this.showAddUserModal}>Add user</ButtonWithDisabledTooltip>
                {usersRendered}
                <br/>
            </Container>
        );
    }
}

// Wrap the component so that multiple contexts are available via the props.
export const UserSettings = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const notificationContext = useContext(NotificationContext);
    const tenantContext = useContext(TenantContext);
  
    return (
        <UserSettingsInternal authenticationContext={authenticationContext} notificationContext={notificationContext} tenantContext={tenantContext} {...props}/>
    )
}